import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, message } from 'antd';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { Box, Flex } from 'atoms';
import { BulkUploadModal } from 'componentsv2/modals';
import {
	createVehicleTypeV2,
	updateVehicleTypeV2,
	getVehicleTypeByIdV2,
	deleteVehicleTypeV2,
	searchVehicleTypesV2,
} from 'store/vehicleSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { bulkUploadVehicleTypes } from 'store/bulkUploadSlice';
import VehicleTypeForm from './VehicleTypeForm';
import VehicleDetailsTable from './VehicleDetailsTable';
import VehicleInfoModal from './VehicleInfoModal';
import {
	GlobalSelectStyle,
	PageHeader,
	BackButton,
	PageTitle,
	PageSubtitle,
	TransferButton,
	EditButton,
	PageContainer,
	ContentWrapper,
	BulkUploadButton,
	SubmitButton,
} from './CreateVehicleTypePageV2.styles';
import {
	VEHICLE_TYPE_LIST_ROUTE,
	BULK_UPLOAD_MESSAGE_KEY,
	BULK_UPLOAD_PROCESS_MESSAGE_KEY,
	BULK_UPLOAD_CONFIG,
} from './CreateVehicleTypePageV2.constants';

export default function CreateVehicleTypePageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams(); // Get vehicle type id from URL if in edit mode
	const [form] = Form.useForm();
	const [modalForm] = Form.useForm();
	const [vehicleDetails, setVehicleDetails] = useState([]);
	const [selectedMaker, setSelectedMaker] = useState('');
	const [modelOptions, setModelOptions] = useState([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [modalMaker, setModalMaker] = useState('');
	const [modalModelOptions, setModalModelOptions] = useState([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [loading, setLoading] = useState(false);
	const [vehicleModelsByMaker, setVehicleModelsByMaker] = useState({});
	const [makerOptions, setMakerOptions] = useState([]);
	// Bulk upload state
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
	const [uploadedS3Key, setUploadedS3Key] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState(null);

	// Helper: Build maker/model mapping from vehicle types
	const buildMakerModelMapping = (vehicleTypes) => {
		const makerModelMap = {};
		const uniqueMakers = new Set();

		vehicleTypes.forEach((vehicleType) => {
			const maker = vehicleType.maker_manufacturer;
			const model = vehicleType.vehicle_type_name;

			if (maker && model) {
				uniqueMakers.add(maker);

				if (!makerModelMap[maker]) {
					makerModelMap[maker] = [];
				}

				if (!makerModelMap[maker].includes(model)) {
					makerModelMap[maker].push(model);
				}
			}
		});

		return { makerModelMap, uniqueMakers };
	};

	// Helper: Populate form with vehicle type data
	const populateFormWithVehicleType = (vehicleType) => {
		form.setFieldsValue({
			maker: vehicleType.maker_manufacturer,
			modelName: vehicleType.vehicle_type_name,
			capacity: vehicleType.capacity_tonnes,
			length: vehicleType.length_feet,
			tempControlled: vehicleType.temperature_controlled ? 'true' : 'false',
			driverMode: vehicleType.driver_mode,
			fuelType: vehicleType.fuel_type,
		});

		if (vehicleType.maker_manufacturer) {
			setSelectedMaker(vehicleType.maker_manufacturer);
			setModelOptions(vehicleModelsByMaker[vehicleType.maker_manufacturer] || []);
		}
	};

	// Fetch vehicle types and build maker/model options
	useEffect(() => {
		const fetchVehicleTypes = async () => {
			try {
				const response = await dispatch(
					searchVehicleTypesV2({
						filter_by: { is_active: true },
						pagination: { page: 1, per_page: 1000 },
					})
				);

				if (response?.rows) {
					const { makerModelMap, uniqueMakers } = buildMakerModelMapping(response.rows);
					setVehicleModelsByMaker(makerModelMap);
					setMakerOptions(Array.from(uniqueMakers).sort());
				}
			} catch (error) {
				console.error('Error fetching vehicle types:', error);
				message.error('Failed to load manufacturer and model options');
			}
		};

		fetchVehicleTypes();
	}, [dispatch]);

	// Load existing vehicle type data if in edit mode
	useEffect(() => {
		if (!id) return;

		setIsEditMode(true);
		setLoading(true);

		dispatch(getVehicleTypeByIdV2({ id }))
			.then((vehicleType) => {
				if (vehicleType) {
					populateFormWithVehicleType(vehicleType);
				} else {
					message.error('Vehicle type not found');
					navigate(VEHICLE_TYPE_LIST_ROUTE);
				}
			})
			.catch((error) => {
				console.error('Error loading vehicle type:', error);
				message.error('Failed to load vehicle type');
				navigate(VEHICLE_TYPE_LIST_ROUTE);
			})
			.finally(() => {
				setLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const handleBack = () => {
		navigate(VEHICLE_TYPE_LIST_ROUTE);
	};

	const handleMakerChange = (value) => {
		setSelectedMaker(value);
		setModelOptions(vehicleModelsByMaker[value] || []);
		// Clear the model name when maker changes
		form.setFieldsValue({ modelName: undefined });
	};

	const handleAddVehicle = () => {
		form.validateFields()
			.then((values) => {
				const newVehicle = {
					key: Date.now(),
					maker: values.maker,
					modelName: values.modelName,
					capacity: parseFloat(values.capacity),
					length: parseFloat(values.length),
					tempControlled: values.tempControlled,
					fuelType: values.fuelType,
					driverMode: values.driverMode,
				};

				setVehicleDetails([...vehicleDetails, newVehicle]);
				form.resetFields();
				setSelectedMaker(null);
				setModelOptions([]);
				message.success('Vehicle added to the list');
			})
			.catch((error) => {
				console.error('Validation failed:', error);
			});
	};

	const handleView = (record) => {
		setSelectedVehicle(record);
		setModalMaker(record.maker);
		setModalModelOptions(vehicleModelsByMaker[record.maker] || []);
		modalForm.setFieldsValue({
			maker: record.maker,
			modelName: record.modelName,
			capacity: record.capacity,
			length: record.length,
			tempControlled: record.tempControlled,
			fuelType: record.fuelType,
			driverMode: record.driverMode,
		});
		setIsModalVisible(true);
	};

	const handleModalMakerChange = (value) => {
		setModalMaker(value);
		setModalModelOptions(vehicleModelsByMaker[value] || []);
		modalForm.setFieldsValue({ modelName: undefined });
	};

	// Helper: Build update data from form values
	const buildUpdateData = (values, vehicleId, isActive) => ({
		id: vehicleId,
		vehicle_type_name: `${values.maker} ${values.modelName}`,
		maker_manufacturer: values.maker,
		capacity_tonnes: parseFloat(values.capacity),
		length_feet: parseFloat(values.length),
		temperature_controlled: values.tempControlled === 'true' || values.tempControlled === true,
		driver_mode: values.driverMode,
		fuel_type: values.fuelType,
		is_active: isActive !== undefined ? isActive : true,
	});

	// Helper: Handle API error response
	const handleApiError = (response) => {
		const errorMessage = response?.message || 'Failed to update vehicle type';
		const errorMessages = {
			NOT_FOUND: 'Vehicle type not found',
			DUPLICATE_ENTRY: 'A vehicle type with this name already exists',
			IN_USE: 'Cannot deactivate vehicle type that is currently in use',
			VALIDATION_ERROR: 'Invalid data provided. Please check your inputs.',
		};
		message.error(errorMessages[response?.code] || errorMessage);
	};

	// Helper: Update local vehicle details
	const updateLocalVehicleDetails = (values, responseData = null) => {
		const updatedVehicles = vehicleDetails.map((vehicle) => {
			if (vehicle.key === selectedVehicle.key || vehicle.id === selectedVehicle.id) {
				return {
					...vehicle,
					...values,
					...(responseData || {}),
				};
			}
			return vehicle;
		});
		setVehicleDetails(updatedVehicles);
	};

	// Helper: Close modal and reset
	const closeModalAndReset = () => {
		setIsModalVisible(false);
		modalForm.resetFields();
	};

	const handleSaveChanges = async () => {
		try {
			const values = await modalForm.validateFields();

			if (selectedVehicle.id) {
				// Update existing vehicle type via API
				const updateData = buildUpdateData(
					values,
					selectedVehicle.id,
					selectedVehicle.is_active
				);

				const response = await dispatch(
					updateVehicleTypeV2({
						req_data: updateData,
						setErrors: () => {},
					})
				);

				if (response && response.status === 'success') {
					updateLocalVehicleDetails(values, response.data);
					message.success('Vehicle type updated successfully');
					closeModalAndReset();
				} else {
					handleApiError(response);
				}
			} else {
				// Update vehicle in local array (not yet created)
				updateLocalVehicleDetails(values);
				message.success('Vehicle details updated successfully');
				closeModalAndReset();
			}
		} catch (error) {
			console.error('Validation or update failed:', error);
			message.error('Failed to save changes');
		}
	};

	const handleRemoveVehicle = async () => {
		if (selectedVehicle.id) {
			// Edit mode - call API to deactivate
			try {
				const response = await dispatch(deleteVehicleTypeV2({ id: selectedVehicle.id }));

				if (response?.status === 'success') {
					setIsModalVisible(false);
					modalForm.resetFields();
					navigate(VEHICLE_TYPE_LIST_ROUTE);
				}
			} catch (error) {
				console.error('Error removing vehicle type:', error);
			}
		} else {
			// Create mode - just remove from local table
			setVehicleDetails(vehicleDetails.filter((item) => item.key !== selectedVehicle.key));
			message.success('Vehicle removed from list');
			setIsModalVisible(false);
			modalForm.resetFields();
		}
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
		setSelectedVehicle(null);
		modalForm.resetFields();
	};

	const createVehicleType = async (vehicle) => {
		const requestData = {
			vehicle_type_name: `${vehicle.maker} ${vehicle.modelName}`,
			maker_manufacturer: vehicle.maker,
			capacity_tonnes: parseFloat(vehicle.capacity),
			length_feet: parseFloat(vehicle.length),
			temperature_controlled: vehicle.tempControlled === 'true',
			driver_mode: vehicle.driverMode,
			fuel_type: vehicle.fuelType,
		};

		return dispatch(
			createVehicleTypeV2({
				req_data: requestData,
				setErrors: () => {},
			})
		)
			.then((response) => ({
				success: response?.status === 'success',
				name: vehicle.modelName,
				message: response?.message,
			}))
			.catch((error) => ({
				success: false,
				name: vehicle.modelName,
				message: error?.message || 'API call failed',
			}));
	};

	const processResults = (results, successCount, failedCount) => {
		if (successCount > 0 && failedCount === 0) {
			message.success(`All ${successCount} vehicle type(s) created successfully`);
			setVehicleDetails([]);
			navigate(VEHICLE_TYPE_LIST_ROUTE);
		} else if (successCount > 0 && failedCount > 0) {
			message.warning(`${successCount} succeeded, ${failedCount} failed. Check details.`);
			const successfulNames = results.filter((r) => r.success).map((r) => r.name);
			setVehicleDetails(vehicleDetails.filter((v) => !successfulNames.includes(v.modelName)));
		} else {
			message.error('All vehicle types failed to create. Please check and try again.');
		}
	};

	// Helper: Handle create mode
	const handleCreateMode = async () => {
		try {
			const results = await Promise.all(vehicleDetails.map(createVehicleType));
			const successCount = results.filter((r) => r.success).length;
			const failedCount = results.length - successCount;

			processResults(results, successCount, failedCount);
		} catch (error) {
			console.error('Submit error:', error);
			message.error('An error occurred while submitting vehicles');
		}
	};

	// Helper: Handle form submission in edit mode
	const handleEditModeSubmit = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const updateData = {
				id,
				vehicle_type_name: values.modelName,
				maker_manufacturer: values.maker,
				capacity_tonnes: parseFloat(values.capacity),
				length_feet: parseFloat(values.length),
				temperature_controlled: values.tempControlled === 'true',
				driver_mode: values.driverMode,
				fuel_type: values.fuelType,
				is_active: true,
			};

			const response = await dispatch(
				updateVehicleTypeV2({
					req_data: updateData,
					setErrors: () => {},
				})
			);

			if (response && response.status === 'success') {
				message.success('Vehicle type updated successfully');
				navigate(VEHICLE_TYPE_LIST_ROUTE);
			} else {
				const errorMessage = response?.message || 'Failed to update vehicle type';
				message.error(errorMessage);
			}
		} catch (error) {
			console.error('Update error:', error);
			if (error.errorFields) {
				message.error('Please fill in all required fields correctly.');
			} else {
				message.error('An error occurred while updating vehicle type');
			}
		} finally {
			setLoading(false);
		}
	};

	// Handle bulk upload
	const handleBulkUpload = () => {
		console.log('🚀 [Bulk Upload] Opening modal');
		setBulkUploadModalVisible(true);
	};

	// Handle bulk upload modal close
	const handleBulkUploadClose = () => {
		console.log('❌ [Bulk Upload] Closing modal');
		setBulkUploadModalVisible(false);
		setUploadedS3Key(null);
		setUploadedFileName(null);
		setBulkUploadLoading(false);
	};

	// Handle file upload to S3 (called when file is selected in the modal)
	const handleBulkUploadFileToS3 = async (file) => {
		console.log('📤 [Bulk Upload] Step 1: Uploading file to S3...', { fileName: file.name });
		setBulkUploadLoading(true);
		message.loading({
			content: 'Uploading file to S3...',
			key: BULK_UPLOAD_MESSAGE_KEY,
		});

		try {
			// Step 1: Get signed URL for S3 upload
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: BULK_UPLOAD_CONFIG.entity,
						keys: [file.name],
						content_type: 'text/csv',
					},
				})
			);

			console.log('🔑 [Bulk Upload] Signed URL response:', signedUrlResponse);

			if (!signedUrlResponse || signedUrlResponse.status !== 'success') {
				throw new Error('Failed to get S3 upload URL');
			}

			const uploadData = signedUrlResponse.data?.[0];
			if (!uploadData?.url || !uploadData?.key) {
				throw new Error('Invalid S3 upload URL response');
			}

			const { url: uploadUrl, key: s3KeyFromBackend } = uploadData;

			// Construct the full S3 key with entity prefix
			// Backend might return just the filename, so we ensure we have the full path
			const fullS3Key = s3KeyFromBackend.includes('/')
				? s3KeyFromBackend
				: `${BULK_UPLOAD_CONFIG.entity}/${s3KeyFromBackend}`;

			console.log('📤 [Bulk Upload] Step 2: Uploading to S3...', {
				s3KeyFromBackend,
				fullS3Key,
				uploadUrl: `${uploadUrl.substring(0, 50)}...`,
			});

			// Step 2: Upload file to S3 using signed URL
			const uploadResponse = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
			}

			// Store the full S3 key for later submission
			setUploadedS3Key(fullS3Key);
			setUploadedFileName(file.name);

			console.log('✅ [Bulk Upload] File uploaded to S3 successfully', { fullS3Key });
			message.success({
				content: 'File uploaded successfully. Click Submit to process.',
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 3,
			});
		} catch (error) {
			console.error('❌ [Bulk Upload] S3 upload failed:', error);
			message.error({
				content: `Failed to upload file: ${error.message}`,
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 5,
			});
		} finally {
			setBulkUploadLoading(false);
		}
	};

	// Handle bulk upload submit (called when Submit button is clicked)
	const handleBulkUploadSubmit = async () => {
		if (!uploadedS3Key) {
			message.error('Please upload a file first');
			return;
		}

		console.log('📤 [Bulk Upload] Step 3: Processing bulk upload...', {
			s3Key: uploadedS3Key,
			fileName: uploadedFileName,
		});

		setBulkUploadLoading(true);
		message.loading({
			content: 'Processing bulk upload...',
			key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
		});

		try {
			// Step 3: Call bulk upload API with S3 key
			const response = await dispatch(
				bulkUploadVehicleTypes({
					s3_key: uploadedS3Key,
					setErrors: (errors) => {
						console.error('Bulk upload errors:', errors);
					},
				})
			);

			console.log('📊 [Bulk Upload] Response:', response);

			if (response && response.status === 'success') {
				const { success_count, error_count, total_count, download_url } =
					response.data || {};

				message.success({
					content: `Bulk upload completed: ${success_count} succeeded${
						error_count > 0 ? `, ${error_count} failed` : ''
					} out of ${total_count} total`,
					key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
					duration: 5,
				});

				// If there are errors, show option to download error report
				if (error_count > 0 && download_url) {
					message.warning({
						content: (
							<span>
								Some vehicle types failed to upload.{' '}
								<a href={download_url} target="_blank" rel="noopener noreferrer">
									Download error report
								</a>
							</span>
						),
						duration: 10,
					});
				}

				// Close modal and refresh page
				handleBulkUploadClose();
				message.info('Please refresh the page to see updated vehicle types');
			} else {
				throw new Error(response?.error?.message || 'Bulk upload failed');
			}
		} catch (error) {
			console.error('❌ [Bulk Upload] Processing failed:', error);
			message.error({
				content: `Failed to process bulk upload: ${error.message}`,
				key: BULK_UPLOAD_PROCESS_MESSAGE_KEY,
				duration: 5,
			});
		} finally {
			setBulkUploadLoading(false);
		}
	};

	const handleSubmit = async () => {
		if (isEditMode) {
			await handleEditModeSubmit();
		} else {
			// Create mode: use vehicle details table
			if (vehicleDetails.length === 0) {
				message.warning('Please add at least one vehicle');
				return;
			}
			await handleCreateMode();
		}
	};

	const handleTransfer = () => {
		message.info('Transfer vehicle feature coming soon');
	};

	return (
		<PageContainer>
			<GlobalSelectStyle />
			<ContentWrapper>
				<PageHeader justifyContent="space-between">
					<Flex alignItems="center" gap="12px">
						<BackButton onClick={handleBack}>
							<ArrowLeftOutlined style={{ fontSize: '20px' }} />
						</BackButton>
						<Box>
							<PageTitle>Vehicles</PageTitle>
							<PageSubtitle>
								{isEditMode ? 'Edit Vehicle Type' : 'Manage Vehicles here'}
							</PageSubtitle>
						</Box>
					</Flex>
					{isEditMode && (
						<Flex gap="12px">
							<TransferButton onClick={handleTransfer}>Transfer</TransferButton>
							<EditButton type="primary">Edit</EditButton>
						</Flex>
					)}
				</PageHeader>
				{/* Show form in both create and edit modes */}
				{!isEditMode && (
					<Flex justifyContent="flex-end" mb={2}>
						<BulkUploadButton onClick={handleBulkUpload}>
							<UploadOutlined />
							Bulk Upload
						</BulkUploadButton>
					</Flex>
				)}

				{/* Form Section */}
				<VehicleTypeForm
					form={form}
					isEditMode={isEditMode}
					makerOptions={makerOptions}
					modelOptions={modelOptions}
					selectedMaker={selectedMaker}
					loading={loading}
					handleMakerChange={handleMakerChange}
					handleAddVehicle={handleAddVehicle}
					handleBack={handleBack}
					handleSubmit={handleSubmit}
				/>
				{/* Vehicle Details Table - Only show in create mode */}
				{!isEditMode && (
					<>
						<VehicleDetailsTable
							vehicleDetails={vehicleDetails}
							handleView={handleView}
						/>

						<Flex justifyContent="flex-end" mt={2}>
							<SubmitButton type="primary" onClick={handleSubmit} loading={loading}>
								Submit
							</SubmitButton>
						</Flex>
					</>
				)}

				{/* Vehicle Info Modal */}
				<VehicleInfoModal
					isModalVisible={isModalVisible}
					modalForm={modalForm}
					makerOptions={makerOptions}
					modalModelOptions={modalModelOptions}
					modalMaker={modalMaker}
					handleModalMakerChange={handleModalMakerChange}
					handleSaveChanges={handleSaveChanges}
					handleRemoveVehicle={handleRemoveVehicle}
					handleModalClose={handleModalClose}
				/>

				{/* Bulk Upload Modal */}
				<BulkUploadModal
					visible={bulkUploadModalVisible}
					onClose={handleBulkUploadClose}
					onSubmit={handleBulkUploadSubmit}
					onFileUpload={handleBulkUploadFileToS3}
					title={BULK_UPLOAD_CONFIG.title}
					description={BULK_UPLOAD_CONFIG.description}
					templateUrl="/vehicle_type_bulk_upload_template_v2.csv"
					templateText={BULK_UPLOAD_CONFIG.templateText}
					acceptedFormats={BULK_UPLOAD_CONFIG.acceptedFormats}
					maxSizeMB={BULK_UPLOAD_CONFIG.maxSizeMB}
					loading={bulkUploadLoading}
				/>
			</ContentWrapper>
		</PageContainer>
	);
}
